import type {DocumentData, QueryDocumentSnapshot} from 'firebase/firestore';
import {useCallback, useEffect, useRef, useState} from 'react';
import {env} from '../../../config/env';
import mapFirestoreDocs from '../../../helper/firebase/mapFirestoreDocs';
import {FirestoreCursor} from '../../../types/FirestoreCursor';
import {FirestoreCursorStore} from '../../../types/FirestoreCursorStore';
import buildFirestoreCursorStore from '../../../utils/cursor/buildFirestoreCursorStore';
import {useFunctionWrapV2} from './useFunctionWrapV2';

export interface UseFunctionPaginationResult<TApp extends DocumentData = DocumentData> {
  data: TApp[];
  /** 0-based current page. */
  pageIndex: number;
  /** `ceil(docTotal / limit)` when `docTotal` is known. */
  // pageTotal: number | undefined;
  /** Total matching documents (same filters / order as the list query, without cursor). */
  docTotal: number | undefined;
  /** In-memory boundaries for pages visited this session (page index → first/last snapshots). */
  // pageBoundaries: ReadonlyMap<number, FirestoreCursorStore>;
  handleNextPage: () => void;
  handlePrevPage: () => void;
  handleFirstPage: () => void;
  /**
   * Loads a page by index using chained `getDocs` from page 0 (or one query when page `target - 1` is cached).
   * Resolves when the requested page (or the last available page if out of range) is loaded.
   * This can be expensive if the page is far away from the current page.
   */
  // handleGotoPage: (pageIndex: number) => Promise<void>;
  canGoNext: boolean;
  canGoPrev: boolean;
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<void>;
  clearData: () => void;
}

export interface UseFunctionPaginationOptions {
  /** skip fetching */
  skip?: boolean;
  /** function to fetch the total count of the list */
  countFn?: () => Promise<number>;
}

export default function useFunctionPagination<AppData extends DocumentData, DbData extends DocumentData>(
  listFn: (cursor: FirestoreCursor) => Promise<QueryDocumentSnapshot<AppData, DbData>[]>,
  mapDoc: (doc: QueryDocumentSnapshot<AppData, DbData>) => AppData | undefined,
  options: UseFunctionPaginationOptions,
): UseFunctionPaginationResult<AppData> {
  const {skip, countFn} = options;

  const countFnRef = useRef(countFn);
  countFnRef.current = countFn;
  const [data, setData] = useState<AppData[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [docTotal, setDocTotal] = useState<number | undefined>(undefined);
  const [pageLimit, setPageLimit] = useState<number>(0);

  const boundariesRef = useRef<Map<number, FirestoreCursorStore>>(new Map());
  const {fn: runListFn, loading: listLoading, error: listError, data: listData} = useFunctionWrapV2(listFn);
  const lastCursorRef = useRef<FirestoreCursor | undefined>(undefined);

  const currentBoundary = boundariesRef.current.get(pageIndex);

  // fetch count
  useEffect(() => {
    if (!countFnRef.current) return;
    if (docTotal !== undefined) return;
    if (skip) return;
    countFnRef
      .current?.()
      .then((count) => {
        setDocTotal(count);
      })
      .catch((err) => {
        if (env.isDev) {
          console.error('useFunctionPagination count error:', err);
        }
        setDocTotal(undefined);
      });
  }, [skip, docTotal]);

  // change page
  useEffect(() => {
    if (skip) return;
    if (pageIndex === 0) {
      lastCursorRef.current = {startAfter: undefined};
      runListFn(lastCursorRef.current);
      return;
    }

    const lastBoundary = boundariesRef.current.get(pageIndex - 1);
    // we have this boundary in memory/map
    if (lastBoundary) {
      lastCursorRef.current = {startAfter: lastBoundary.last};
      runListFn(lastCursorRef.current);
      return;
    }
    console.error('useFunctionPagination no boundary found');
    // we don't have this boundary in memory/map, so we need to fetch the previous page
    // runListFn({startAfter: currentBoundary?.startAfter});
  }, [pageIndex, skip, runListFn]);

  // processing data change
  useEffect(() => {
    if (!listData) {
      setData([]);
      return;
    }
    // save data to state
    const data = mapFirestoreDocs(listData, mapDoc);
    setData(data);

    // use data size as limit if not defined
    const limit = pageLimit === 0 ? data.length : pageLimit;
    setPageLimit(limit);
    const boundaries = buildFirestoreCursorStore(listData, pageIndex, limit, docTotal);
    boundariesRef.current.set(pageIndex, boundaries);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listData]);

  // console log error
  useEffect(() => {
    if (env.isDev && listError) {
      console.error('useFunctionPagination list error:', listError);
    }
  }, [listError]);

  /////////////////////////////processing functions/////////////////////////////
  const canGoNext =
    currentBoundary?.last != undefined &&
    data.length === pageLimit &&
    (docTotal === undefined || (pageIndex + 1) * pageLimit < docTotal);

  const canGoPrev = pageIndex > 0;

  const handleNextPage = useCallback(() => {
    if (!canGoNext) {
      return;
    }
    setPageIndex((i) => i + 1);
  }, [canGoNext]);

  const handlePrevPage = useCallback(() => {
    if (!canGoPrev) {
      return;
    }
    setPageIndex((i) => Math.max(0, i - 1));
  }, [canGoPrev]);

  const handleFirstPage = useCallback(() => {
    setPageIndex(0);
  }, []);

  const clearData = useCallback(() => {
    setData([]);
    setPageIndex(0);
    setDocTotal(undefined);
    boundariesRef.current = new Map();
  }, []);

  const loading = listLoading;
  const error = listError;

  return {
    data,
    pageIndex,
    docTotal,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    canGoNext,
    canGoPrev,
    loading,
    error,
    refetch: () => runListFn(lastCursorRef.current),
    clearData,
  };
}
